import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { UnlistedStockStatus } from '../../types';
import type { User, UnlistedStock } from '../../types';

// Simplified types for internal use if not imported from shared
// Assuming types are available via imports, otherwise re-defining or using any
interface AdminProps {
    user: User | null;
}

const UnlistedStocksAdmin: React.FC<AdminProps> = ({ user }) => {
    const hasAdminAccess = user?.isGrowthAdmin || user?.isUnlistedAdmin;

    const [stocks, setStocks] = useState<UnlistedStock[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchStocks();
    }, []);

    const fetchStocks = async () => {
        try {
            const response = await api.get('/unlisted-stocks/admin/all');
            if (response.data.success) {
                setStocks(response.data.data);
            }
        } catch (err) {
            setError('Failed to fetch stocks');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (id: string, newStatus: UnlistedStockStatus) => {
        try {
            await api.patch(`/unlisted-stocks/${id}/status`, { status: newStatus });
            fetchStocks(); // Refresh details
        } catch (err) {
            alert('Failed to update status');
            console.error(err);
        }
    };

    if (loading) return <div className="p-8">Loading...</div>;
    if (error) return <div className="p-8 text-red-600">{error}</div>;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-slate-900">Unlisted Stocks Administration</h1>
                {hasAdminAccess && (
                    <Link to="/admin/unlisted-stocks/new" className="btn btn-primary px-4 py-2 rounded-lg">
                        + Add New Stock
                    </Link>
                )}
            </div>

            <div className="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden">
                <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Symbol</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Price</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                        {stocks.map((stock) => (
                            <tr key={stock.id} className="hover:bg-slate-50">
                                <td className="px-6 py-4 whitespace-nowrap font-medium text-slate-900">{stock.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-slate-500">{stock.symbol}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-slate-900">₹{stock.price}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                        ${stock.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                                            stock.status === 'HIDDEN' ? 'bg-gray-100 text-gray-800' :
                                                'bg-yellow-100 text-yellow-800'}`}>
                                        {stock.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <Link to={`/admin/unlisted-stocks/${stock.id}/edit`} className="text-indigo-600 hover:text-indigo-900 mr-4">
                                        Edit
                                    </Link>

                                    {stock.status !== UnlistedStockStatus.HIDDEN && (
                                        <button onClick={() => handleStatusChange(stock.id, UnlistedStockStatus.HIDDEN)} className="text-red-600 hover:text-red-900">
                                            Hide
                                        </button>
                                    )}
                                    {stock.status === UnlistedStockStatus.HIDDEN && (
                                        <button onClick={() => handleStatusChange(stock.id, UnlistedStockStatus.ACTIVE)} className="text-green-600 hover:text-green-900">
                                            Activate
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UnlistedStocksAdmin;
